import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Group Store - stores the currently selected group
 * Used across the application for group-scoped API calls
 */

interface Group {
    id: number;
    name?: string;
    description?: string;
}

interface GroupState {
    selectedGroup: Group | null;
    groups: Group[];
    isLoading: boolean;
}

interface GroupActions {
    setSelectedGroup: (group: Group | null) => void;
    setGroups: (groups: Group[]) => void;
    setLoading: (isLoading: boolean) => void;
    selectGroupById: (groupId: number) => void;
    clearGroup: () => void;
}

type GroupStore = GroupState & GroupActions;

const initialState: GroupState = {
    selectedGroup: null,
    groups: [],
    isLoading: false,
};

export const useGroupStore = create<GroupStore>()(
    persist(
        (set, get) => ({
            ...initialState,

            setSelectedGroup: (group) => set({ selectedGroup: group }),

            setGroups: (groups) => {
                set({ groups });
                // Auto-select first group if no group is selected
                const current = get().selectedGroup;
                if (!current && groups.length > 0) {
                    set({ selectedGroup: groups[0] });
                }
            },

            setLoading: (isLoading) => set({ isLoading }),

            selectGroupById: (groupId) => {
                const groups = get().groups;
                const found = groups.find(g => g.id === groupId);
                if (found) {
                    set({ selectedGroup: found });
                }
            },

            clearGroup: () => set(initialState),
        }),
        {
            name: 'tailody-group-storage',
            partialize: (state) => ({
                selectedGroup: state.selectedGroup,
                groups: state.groups,
            }),
        }
    )
);

// Selectors
export const selectSelectedGroup = (state: GroupStore) => state.selectedGroup;
export const selectSelectedGroupId = (state: GroupStore) => state.selectedGroup?.id;
export const selectGroups = (state: GroupStore) => state.groups;
